import React, { createContext, useContext, useEffect } from 'react';
import { useMemoryWatchdog } from '@/hooks/useMemoryWatchdog';
import { usePageVisibility } from '@/hooks/usePageVisibility';
import { useQueryClient } from '@tanstack/react-query';

interface MemoryWatchdogContextType {
  memoryUsage: number | null;
  forceCleanup: () => number;
  isVisible: boolean;
}

const MemoryWatchdogContext = createContext<MemoryWatchdogContextType | null>(null);

export const useMemoryContext = () => {
  const context = useContext(MemoryWatchdogContext);
  if (!context) {
    throw new Error('useMemoryContext must be used within MemoryWatchdogProvider');
  }
  return context;
};

interface MemoryWatchdogProviderProps {
  children: React.ReactNode;
}

/**
 * Provider that manages memory optimization across the app
 * - Monitors memory usage
 * - Cleans up cache when needed
 * - Pauses operations when tab is hidden
 */
export const MemoryWatchdogProvider: React.FC<MemoryWatchdogProviderProps> = ({ children }) => {
  const { memoryUsage, forceCleanup } = useMemoryWatchdog();
  const { isVisible, hasBeenHiddenFor } = usePageVisibility();
  const queryClient = useQueryClient();

  // When tab becomes visible after being hidden for a while, do a soft refresh
  useEffect(() => {
    if (isVisible) {
      // If hidden for more than 5 minutes, invalidate stale queries
      const wasHiddenLong = hasBeenHiddenFor(5 * 60 * 1000);
      if (wasHiddenLong) {
        
        // Only invalidate queries that are currently being observed
        queryClient.invalidateQueries({
          predicate: (query) => query.getObserversCount() > 0,
        });
      }
    }
  }, [isVisible, hasBeenHiddenFor, queryClient]);

  // Cleanup on unmount or before unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      forceCleanup();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      forceCleanup();
    };
  }, [forceCleanup]);

  return (
    <MemoryWatchdogContext.Provider value={{ memoryUsage, forceCleanup, isVisible }}>
      {children}
    </MemoryWatchdogContext.Provider>
  );
};
